/**
 * NPCs and enemies in the world.
 *
 * NPCs are static, so they are built once per zone. Enemies move constantly, so
 * their meshes are created once and then repositioned — rebuilding them every
 * frame is the quickest way to make a busy zone unplayable on a phone.
 */

import type { Scene } from '@babylonjs/core/scene.js';
import type { Camera } from '@babylonjs/core/Cameras/camera.js';
import type { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator.js';
import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector.js';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder.js';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial.js';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';

import type { NpcView } from '../api/gameTypes.js';
import { buildBeast, buildFigure, poseWalk, type FigureRig } from './figure.js';

const HAIR_STYLES = ['short', 'cropped', 'wavy', 'curls', 'long', 'braided', 'tied-back', 'shaved'];
const BODY_TYPES = ['slight', 'average', 'broad'];

/**
 * A small stable hash of the NPC key.
 *
 * NPCs come from the server with a skin tone, hair colour and outfit but no
 * hairstyle or build. Picking those at random would reshuffle the blacksmith's
 * hair every time the zone reloaded; deriving them from the key means Merrin
 * looks like Merrin for good, without adding two columns to a table for the
 * sake of it.
 */
function hashKey(key: string): number {
  let hash = 2166136261;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash);
}

export interface EnemyView {
  id: string;
  enemyKey: string;
  name: string;
  level: number;
  x: number;
  y: number;
  z: number;
  facing: number;
  health: number;
  maxHealth: number;
  mode: string;
  tint: string;
}

interface Visual {
  root: TransformNode;
  plate: HTMLElement;
  bar: HTMLElement | null;
  /** Present on everything with limbs, which is now everything. */
  rig: FigureRig | null;
  /** Enemies only: where they were last frame, for their gait speed. */
  last?: { x: number; z: number };
}

/**
 * An invisible box the mouse can hit.
 *
 * The figures themselves are unpickable on purpose — a hand or a hair lump
 * catching a ray is a bad click target, and the camera's own collision check
 * must never see a person. So each actor gets one plain box the size of a body,
 * invisible, pickable, carrying the id in its metadata. Clicking is then a
 * single `scene.pick` with no special cases, and the thing you clicked is
 * whatever the ray hit.
 */
function pickProxy(
  scene: Scene,
  name: string,
  size: { width: number; height: number; depth: number },
  y: number,
  metadata: Record<string, string>,
  parent: TransformNode
): void {
  const proxy = MeshBuilder.CreateBox(name, size, scene);
  proxy.position.y = y;
  // isVisible false still takes part in picking; visibility 0 would not have
  // been enough on its own because the mesh would still be submitted to draw.
  proxy.isVisible = false;
  proxy.isPickable = true;
  proxy.metadata = metadata;
  proxy.parent = parent;
}

/** Projects a world point to CSS pixels, or null when it is behind the camera. */
function project(
  point: Vector3,
  scene: Scene,
  camera: Camera,
  canvas: HTMLCanvasElement
): { x: number; y: number; distance: number } | null {
  const engine = scene.getEngine();
  const viewport = camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight());
  const projected = Vector3.Project(point, Matrix.Identity(), scene.getTransformMatrix(), viewport);

  // z outside 0..1 is behind the camera; without this check the label
  // reappears mirrored on the opposite side of the screen.
  if (projected.z < 0 || projected.z > 1) return null;

  const scaleX = canvas.clientWidth / engine.getRenderWidth();
  const scaleY = canvas.clientHeight / engine.getRenderHeight();
  return {
    x: projected.x * scaleX,
    y: projected.y * scaleY,
    distance: Vector3.Distance(camera.position, point)
  };
}

export class ActorRenderer {
  private readonly scene: Scene;
  private readonly layer: HTMLElement;
  private readonly shadows: ShadowGenerator | null;
  private readonly npcs = new Map<string, Visual>();
  private readonly enemies = new Map<string, Visual>();
  private npcData: NpcView[] = [];

  constructor(scene: Scene, layer: HTMLElement, shadows: ShadowGenerator | null = null) {
    this.scene = scene;
    this.layer = layer;
    this.shadows = shadows;
  }

  // --- NPCs ----------------------------------------------------------------

  buildNpcs(npcs: NpcView[], groundAt: (x: number, z: number) => number): void {
    this.clearNpcs();
    this.npcData = npcs;

    for (const npc of npcs) {
      const root = new TransformNode(`npc-${npc.key}`, this.scene);
      root.position.set(npc.x, groundAt(npc.x, npc.z), npc.z);
      root.rotation.y = npc.facing;

      /* The same builder the player and everybody else uses. The blacksmith
         being made of the same stuff as you is the whole point — a town of
         capsules with one detailed person in it looks worse than a town of
         capsules. */
      const seed = hashKey(npc.key);
      const rig = buildFigure(this.scene, {
        skinTone: npc.skinTone,
        hairColour: npc.hairColour,
        outfit: npc.outfit,
        hair: HAIR_STYLES[seed % HAIR_STYLES.length]!,
        bodyType: BODY_TYPES[(seed >> 3) % BODY_TYPES.length]!,
        face: `face-${((seed >> 6) % 6) + 1}`
      }, { shadows: this.shadows, name: `npc-${npc.key}` });
      rig.root.parent = root;
      // They stand still, so start each one at a different point in the breath
      // cycle: a row of shopkeepers inhaling in unison is unsettling.
      rig.phase = (seed % 628) / 100;

      // A quiet marker above the head, so somebody worth talking to is
      // findable across a market square without reading every nameplate.
      const marker = MeshBuilder.CreateCylinder(
        `npc-marker-${npc.key}`,
        { diameterTop: 0, diameterBottom: 0.34, height: 0.42, tessellation: 4 },
        this.scene
      );
      marker.position.y = 2.5;
      marker.rotation.x = Math.PI;
      const markerMat = new StandardMaterial(`npc-marker-${npc.key}-mat`, this.scene);
      markerMat.emissiveColor = Color3.FromHexString('#ffc36a');
      markerMat.diffuseColor = Color3.FromHexString('#3a2a10');
      marker.material = markerMat;
      marker.isPickable = false;
      marker.parent = root;

      pickProxy(this.scene, `npc-pick-${npc.key}`,
        { width: 1.1, height: 2, depth: 1.1 }, 1, { npcKey: npc.key }, root);

      const plate = document.createElement('div');
      plate.className = 'nameplate npc';
      plate.textContent = npc.title !== '' ? `${npc.name} · ${npc.title}` : npc.name;
      this.layer.append(plate);

      this.npcs.set(npc.key, { root, plate, bar: null, rig });
    }
  }

  /** One NPC by key, for when the player clicked a specific person. */
  npcByKey(key: string): NpcView | null {
    return this.npcData.find((npc) => npc.key === key) ?? null;
  }

  /** The nearest NPC within talking range, or null. */
  nearestNpc(x: number, z: number, range = 4): NpcView | null {
    let nearest: NpcView | null = null;
    let nearestDistance = range;
    for (const npc of this.npcData) {
      const distance = Math.hypot(npc.x - x, npc.z - z);
      if (distance < nearestDistance) { nearest = npc; nearestDistance = distance; }
    }
    return nearest;
  }

  private clearNpcs(): void {
    for (const visual of this.npcs.values()) {
      visual.root.dispose(false, true);
      visual.plate.remove();
    }
    this.npcs.clear();
    this.npcData = [];
  }

  // --- enemies -------------------------------------------------------------

  private createEnemy(enemy: EnemyView): Visual {
    const root = new TransformNode(`enemy-${enemy.id}`, this.scene);

    /* Four legs, not a standing capsule. Height is the fastest read in a fight:
       anything at knee height is coming for you, anything at head height is a
       person. */
    const rig = buildBeast(this.scene, enemy.tint, {
      shadows: this.shadows,
      name: `enemy-${enemy.id}`
    });
    rig.root.parent = root;

    // Wider than a person and lower: a beast is a long low thing, and a click
    // target that matched a human silhouette would miss most of it.
    pickProxy(this.scene, `enemy-pick-${enemy.id}`,
      { width: 1.3, height: 1.5, depth: 1.9 }, 0.75, { enemyId: enemy.id }, root);

    const plate = document.createElement('div');
    plate.className = 'nameplate enemy';
    this.layer.append(plate);

    const bar = document.createElement('div');
    bar.className = 'enemy-bar';
    bar.innerHTML = '';
    const fill = document.createElement('div');
    fill.className = 'enemy-bar-fill';
    bar.append(fill);
    this.layer.append(bar);

    return { root, plate, bar, rig, last: { x: enemy.x, z: enemy.z } };
  }

  updateEnemies(enemies: Map<string, EnemyView>, dt: number): void {
    for (const [id, visual] of this.enemies) {
      if (!enemies.has(id)) {
        visual.root.dispose(false, true);
        visual.plate.remove();
        visual.bar?.remove();
        this.enemies.delete(id);
      }
    }

    for (const [id, enemy] of enemies) {
      let visual = this.enemies.get(id);
      if (visual === undefined) {
        visual = this.createEnemy(enemy);
        this.enemies.set(id, visual);
      }

      const dead = enemy.mode === 'dead';
      visual.root.setEnabled(!dead);
      visual.root.position.set(enemy.x, enemy.y, enemy.z);
      visual.root.rotation.y = enemy.facing;

      if (visual.rig !== null && visual.last !== undefined) {
        // Gait speed from measured movement, same as the players: a beast held
        // still by a wall should not be running on the spot.
        const moved = dead
          ? 0
          : Math.hypot(enemy.x - visual.last.x, enemy.z - visual.last.z);
        visual.last.x = enemy.x;
        visual.last.z = enemy.z;
        poseWalk(visual.rig, dt > 0 ? moved / dt : 0, dt);
      }
    }

    // NPCs stand where they are put, but they still breathe.
    for (const visual of this.npcs.values()) {
      if (visual.rig !== null) poseWalk(visual.rig, 0, dt);
    }
  }

  /** The nearest living enemy within reach, for the attack prompt. */
  nearestEnemy(
    enemies: Map<string, EnemyView>,
    x: number,
    z: number,
    range = 2.6
  ): EnemyView | null {
    let nearest: EnemyView | null = null;
    let nearestDistance = range;
    for (const enemy of enemies.values()) {
      if (enemy.mode === 'dead') continue;
      const distance = Math.hypot(enemy.x - x, enemy.z - z);
      if (distance < nearestDistance) { nearest = enemy; nearestDistance = distance; }
    }
    return nearest;
  }

  // --- labels --------------------------------------------------------------

  /** Repositions every label. Called once per frame after the camera moves. */
  updateLabels(
    camera: Camera,
    canvas: HTMLCanvasElement,
    enemies: Map<string, EnemyView>
  ): void {
    for (const [key, visual] of this.npcs) {
      const npc = this.npcData.find((n) => n.key === key);
      if (npc === undefined) continue;
      const point = new Vector3(visual.root.position.x, visual.root.position.y + 2.9, visual.root.position.z);
      this.place(visual.plate, null, point, camera, canvas, 40);
    }

    for (const [id, visual] of this.enemies) {
      const enemy = enemies.get(id);
      if (enemy === undefined) continue;

      if (enemy.mode === 'dead') {
        visual.plate.style.display = 'none';
        if (visual.bar !== null) visual.bar.style.display = 'none';
        continue;
      }

      visual.plate.textContent = `${enemy.name} · ${enemy.level}`;
      const fill = visual.bar?.firstElementChild as HTMLElement | undefined;
      if (fill !== undefined) {
        const fraction = enemy.maxHealth > 0 ? enemy.health / enemy.maxHealth : 0;
        fill.style.width = `${Math.max(0, Math.min(100, fraction * 100))}%`;
      }

      const point = new Vector3(enemy.x, enemy.y + 2.3, enemy.z);
      this.place(visual.plate, visual.bar, point, camera, canvas, 45);
    }
  }

  private place(
    plate: HTMLElement,
    bar: HTMLElement | null,
    point: Vector3,
    camera: Camera,
    canvas: HTMLCanvasElement,
    maxDistance: number
  ): void {
    const projected = project(point, this.scene, camera, canvas);
    if (projected === null || projected.distance > maxDistance) {
      plate.style.display = 'none';
      if (bar !== null) bar.style.display = 'none';
      return;
    }
    plate.style.display = '';
    plate.style.left = `${projected.x}px`;
    plate.style.top = `${projected.y}px`;

    if (bar !== null) {
      bar.style.display = '';
      bar.style.left = `${projected.x}px`;
      bar.style.top = `${projected.y + 4}px`;
    }
  }

  dispose(): void {
    this.clearNpcs();
    for (const visual of this.enemies.values()) {
      visual.root.dispose(false, true);
      visual.plate.remove();
      visual.bar?.remove();
    }
    this.enemies.clear();
  }
}
